import { Injectable, Inject } from '@angular/core';
import { HttpClient, HttpEvent, HttpParams, HttpRequest, HttpEventType } from '@angular/common/http';
import { Observable } from 'rxjs';
import { HttpErrorHandler } from './errors/http-error-handler.service';
import { catchError, filter, map } from 'rxjs/operators';
import { transformAngularUploadEvent, UploadEvent, UploadEventTypes } from '../../uploads/utils/upload-progress-event';
import { FileEntry } from '../../uploads/file-entry';
import {UploadxService, UploadxOptions, UploadState, Uploader} from 'laravel-ngx-uploadx';
import { getUploadProgress } from 'common/uploads/utils/get-upload-progress';
import { prettyBytes } from '../utils/pretty-bytes';
import { prettyUploadETA } from 'common/uploads/utils/pretty-upload-eta';
import { getUploadSpeed } from 'common/uploads/utils/get-upload-speed';
import { getUploadETA } from 'common/uploads/utils/get-upload-eta';
import { UploaderExt } from './uploader-ext.class';
import { DOCUMENT } from '@angular/common';
import { EncryptionHandler } from 'app/drive/security/encryption-handler';
import { SecurityHandler } from 'app/drive/security/security-handler';

@Injectable({
    providedIn: 'root'
})
export class AppHttpClient {
    static prefix = 'secure';

    constructor(
        protected httpClient: HttpClient,
        protected errorHandler: HttpErrorHandler,
        protected uploadService: UploadxService,
        @Inject(DOCUMENT) protected doc: any
    ) { }

    public get<T>(uri: string, params = {}, options: object = {}): Observable<T> | any {
        const httpParams = this.generateHttpParams(params);
        return this.httpClient.get<T>(this.prefixUri(uri), { params: httpParams, ...options })
            .pipe(catchError(err => this.errorHandler.handle(err, uri)));
    }

    public post<T>(uri: string, params: object = null): Observable<T> | any {
        return this.httpClient.post<T>(this.prefixUri(uri), params)
            .pipe(catchError(err => this.errorHandler.handle(err, uri)));
    }

    public put<T>(uri: string, params: object = {}): Observable<T> | any {
        params = this.spoofHttpMethod(params, 'PUT');
        return this.httpClient.post<T>(this.prefixUri(uri), params)
            .pipe(catchError(err => this.errorHandler.handle(err, uri)));
    }

    public delete<T>(uri: string, params: object = {}): Observable<T> | any {
        params = this.spoofHttpMethod(params, 'DELETE');
        return this.httpClient.post<T>(this.prefixUri(uri), params)
            .pipe(catchError(err => this.errorHandler.handle(err, uri)));
    }

    public uploadWithProgress(uri: string, data: FormData): Observable<UploadEvent> {
        const securityHandler = new SecurityHandler()
        const fileKey = securityHandler.getRandomKey()
        const options: UploadxOptions = {
            endpoint: this.prefixUri(uri),
            chunkSize: 75 * 1024 * 1024,
            token: this.parseCookieValue(this.doc.cookie, 'XSRF-TOKEN'),
            uploaderClass: UploaderExt,
            fileParams: {
                parentId: data.get('parentId') ? data.get('parentId').toString() : null,
                path: data.get('path') ? data.get('path').toString() : null,
                fileKey
            },
            encryptionHandler: EncryptionHandler.getEncryptor(),
            securityHandler: securityHandler
        }
        const uploaderService = this.uploadService.init(options)

        const now = Date.now();

        this.uploadService.handleFile(data.get('file') as File)

        return uploaderService.pipe(
            catchError(err => this.errorHandler.handle(err, uri)),
            map((e: UploadState) => {
                // Filter for file to only display current files progress
                if (e.file == data.get('file') as File) {
                    return this.transformUploadxEvent(e, now)
                }
            }),
            filter(e => !!e)
        );
    }

    private transformUploadxEvent(e: UploadState, uploadStarted: number): UploadEvent {
        switch (e.status) {
            case "added":
                return { type: HttpEventType.Sent, name: UploadEventTypes.STARTED, time: uploadStarted };
            case "uploading":
                const httpEvent: HttpEvent<HttpEventType.UploadProgress> = {
                    type: HttpEventType.UploadProgress,
                    total: e.size,
                    loaded: e.loaded
                }
                return {
                    type: HttpEventType.UploadProgress,
                    name: UploadEventTypes.PROGRESS,
                    totalBytes: httpEvent.total,
                    completedBytes: httpEvent.loaded,
                    progress: getUploadProgress(httpEvent),
                    speed: prettyBytes(getUploadSpeed(httpEvent, uploadStarted)),
                    eta: prettyUploadETA(getUploadETA(httpEvent, uploadStarted)),
                };
            case "complete":
                return { type: HttpEventType.Response, name: UploadEventTypes.COMPLETED, fileEntry: e.response.fileEntry };
            default:
                return null;
        }
    }

    private parseCookieValue(cookieStr: string, name: string): string | null {
        name = encodeURIComponent(name);

        for (const cookie of cookieStr.split(';')) {
            const eqIndex = cookie.indexOf('=');

            const [cookieName, cookieValue]: string[] =
                eqIndex == -1 ? [cookie, ''] : [cookie.slice(0, eqIndex), cookie.slice(eqIndex + 1)];

            if (cookieName.trim() === name) {
                return decodeURIComponent(cookieValue);
            }
        }

        return null;
    }

    public postWithProgress(uri: string, params: object = {}): Observable<UploadEvent> {
        const req = new HttpRequest('POST', this.prefixUri(uri), params, {
            reportProgress: true
        });

        const now = Date.now();

        return this.httpClient.request(req).pipe(
            catchError(err => this.errorHandler.handle(err, uri)),
            map((e: HttpEvent<{ fileEntry: FileEntry }>) => transformAngularUploadEvent(e, now)),
            filter(e => !!e)
        );
    }

    /**
     * Prefix specified uri with backend API prefix.
     */
    private prefixUri(uri: string) {
        if (uri.indexOf('://') > -1) return uri;
        return AppHttpClient.prefix + '/' + uri;
    }

    /**
     * Generate http params for GET request.
     */
    private generateHttpParams(params: object | null) {
        let httpParams = new HttpParams();
        if (!params) return httpParams;

        Object.keys(params).forEach(key => {
            let value = params[key];
            if (value == null) value = '';
            httpParams = httpParams.append(key, value);
        });

        return httpParams;
    }

    /**
     * Spoof http method by adding '_method' to request params.
     */
    private spoofHttpMethod(params: object | FormData, method: 'PUT' | 'DELETE'): object | FormData {
        if (params instanceof FormData) {
            (params as FormData).append('_method', method);
        } else {
            params['_method'] = method;
        }

        return params;
    }
}
